
import { memo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Icon } from '@/components/ui/icon'
import type { IconName } from '@/components/ui/icon'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type AlignDirection = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom'
type DistributeAxis = 'horizontal' | 'vertical'

/** Above the navigation toolbar. `fixed bottom-12` sat in that same lane. */
export const ARRANGEMENT_BAR_CLASS =
  'absolute bottom-28 left-1/2 z-30 -translate-x-1/2 bg-popover border border-border rounded-lg shadow-lg px-3 py-1.5 flex items-center gap-1 pointer-events-auto'

interface MultiSelectionBarProps {
  selectedCount: number
  onAlign: (direction: AlignDirection) => void
  onDistribute: (axis: DistributeAxis) => void
  onDuplicate: () => void
  onDelete: () => void
}

/* ------------------------------------------------------------------ */
/*  Action button                                                      */
/* ------------------------------------------------------------------ */

interface ActionButtonProps {
  icon: IconName
  label: string
  onClick: () => void
  destructive?: boolean
}

function ActionButton({ icon, label, onClick, destructive }: ActionButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          className={`p-1.5 rounded-md hover:bg-accent transition-colors ${
            destructive ? 'hover:text-destructive' : ''
          }`}
        >
          <Icon name={icon} size="sm" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

/* ------------------------------------------------------------------ */
/*  Divider                                                            */
/* ------------------------------------------------------------------ */

function Divider() {
  return <div className="w-px h-4 bg-border mx-0.5" />
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const MultiSelectionBar = memo(function MultiSelectionBar({
  selectedCount,
  onAlign,
  onDistribute,
  onDuplicate,
  onDelete,
}: MultiSelectionBarProps) {
  const show = selectedCount >= 2

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.15 }}
          className={ARRANGEMENT_BAR_CLASS}
        >
          <TooltipProvider delayDuration={100}>
            {/* Selection count */}
            <span className="text-xs text-muted-foreground font-medium px-1.5 tabular-nums">
              {selectedCount} selected
            </span>

            <Divider />

            {/* Align */}
            <ActionButton icon="align-horizontal-justify-start" label="Align left" onClick={() => onAlign('left')} />
            <ActionButton icon="align-horizontal-justify-center" label="Align center" onClick={() => onAlign('center')} />
            <ActionButton icon="align-horizontal-justify-end" label="Align right" onClick={() => onAlign('right')} />
            <ActionButton icon="align-vertical-justify-start" label="Align top" onClick={() => onAlign('top')} />
            <ActionButton icon="align-vertical-justify-center" label="Align middle" onClick={() => onAlign('middle')} />
            <ActionButton icon="align-vertical-justify-end" label="Align bottom" onClick={() => onAlign('bottom')} />

            <Divider />

            {/* Distribute */}
            <ActionButton icon="align-horizontal-space-between" label="Distribute horizontally" onClick={() => onDistribute('horizontal')} />
            <ActionButton icon="align-vertical-space-between" label="Distribute vertically" onClick={() => onDistribute('vertical')} />

            <Divider />

            {/* Actions */}
            <ActionButton icon="copy" label="Duplicate (⌘D)" onClick={onDuplicate} />
            <ActionButton icon="trash-2" label="Delete (⌫)" onClick={onDelete} destructive />
          </TooltipProvider>
        </motion.div>
      )}
    </AnimatePresence>
  )
})
